import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import { cstDateTime, centralTzLabel } from '../lib/time';

function relAge(iso, now, offsetMs = 0) {
  if (!iso) return '—';
  // P12: subtract the client's clock-offset-vs-server so the age column
  // shows the REAL time since the event, not the time since the client's
  // (possibly-skewed) clock thinks the event happened.
  const clientNow = (now?.getTime?.() || Date.now());
  const adjustedNow = clientNow - offsetMs;
  const ms = adjustedNow - new Date(iso).getTime();
  if (ms < -2000) return 'just now';
  if (ms < 1000) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function integrityColor(score) {
  if (score == null) return 'text-[var(--txt-muted)]';
  if (score >= 85) return 'text-buy';
  if (score >= 60) return 'text-hold';
  return 'text-sell';
}

export default function Audit() {
  const [entries, setEntries] = useState([]);
  const [last, setLast] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  // P12: clock-offset between client and server (ms). Fixes the
  // "shows 10m ago when just logged in" bug caused by client clock skew.
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  // Live-updating "now" so relative-age column never drifts from the sidebar clock.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // P12: cache-bust the GET so the browser/CDN never serves a stale list.
  // Combined with the server's Cache-Control: no-store this guarantees
  // that the row for the operator's most recent login always appears.
  const load = async () => {
    const { data } = await api.get(`/auth/audit?limit=200&_t=${Date.now()}`);
    setEntries(data.entries || []);
    setLast(new Date().toISOString());
    // P12: compute client↔server clock offset from server's `now` field.
    // offset > 0 means the client is running ahead of the server.
    if (data.now) {
      try {
        const serverNow = new Date(data.now).getTime();
        setClockOffsetMs(Date.now() - serverNow);
      } catch {}
    }
    try {
      const { data: integ } = await api.get(`/system/integrity?_t=${Date.now()}`);
      setIntegrity(integ.current || null);
    } catch {}
  };
  useEffect(() => {
    load();
    // P11: auto-refresh every 10s so new logins appear without a manual click.
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  // Refresh when the tab becomes visible again (covers "alt-tab back to ATDE").
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const success = entries.filter((e) => e.success).length;
  const failed = entries.length - success;

  const Stat = ({ label, value, color = '', testId }) => (
    <div className="panel p-3" data-testid={testId}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-sys font-display">{label}</div>
      <div className={`font-mono text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );

  const c = integrity?.components || {};

  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-4 gap-3">
        <Stat label="Total Attempts" value={entries.length} testId="audit-stat-total" />
        <Stat label="Granted" value={success} color="text-buy" testId="audit-stat-granted" />
        <Stat label="Denied" value={failed} color="text-sell" testId="audit-stat-denied" />
        <Stat
          label="State Integrity"
          value={integrity?.score != null ? `${integrity.score}/100` : '—'}
          color={integrityColor(integrity?.score)}
          testId="audit-stat-integrity"
        />
      </div>

      {integrity && (
        <CornerPanel title="System Integrity // self-heal diagnostics" testId="integrity-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-cyan-sys">snapshot</div>
              <div className={c.snapshot_ok ? 'text-buy' : 'text-sell'}>
                {c.snapshot_ok ? `OK · ${c.snapshot_age_s}s` : `STALE · ${c.snapshot_age_s ?? '—'}s`}
              </div>
              <div className="text-[var(--txt-muted)] text-[10px]">{c.snapshot_count ?? 0} rows</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-cyan-sys">jobs (1h)</div>
              <div><span className="text-buy">{c.jobs_done_1h ?? 0}</span> / <span className="text-sell">{c.jobs_error_1h ?? 0}</span></div>
              <div className="text-[var(--txt-muted)] text-[10px]">done / error</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-cyan-sys">coverage</div>
              <div className={c.coverage_pct >= 80 ? 'text-buy' : 'text-hold'}>
                {c.coverage_pct ?? 0}%
              </div>
              <div className="text-[var(--txt-muted)] text-[10px]">watchlist / {c.watchlist_size ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-cyan-sys">stuck jobs</div>
              <div className={(c.jobs_stuck ?? 0) === 0 ? 'text-buy' : 'text-sell'}>
                {c.jobs_stuck ?? 0}
              </div>
              <div className="text-[var(--txt-muted)] text-[10px]">
                last heal @ {integrity?.timestamp ? cstDateTime(integrity.timestamp).slice(11) : '—'}
              </div>
            </div>
          </div>
        </CornerPanel>
      )}

      <CornerPanel
        title="Login Audit // full access log"
        right={<RefreshButton onRefresh={load} lastUpdated={last} testId="audit-refresh" />}
        testId="audit-panel"
      >
        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[720px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.25em] text-cyan-sys border-b border-[var(--border-default)]">
                <th className="text-left py-2 px-2">timestamp ({centralTzLabel(now).toLowerCase()})</th>
                <th className="text-left py-2 px-2">age</th>
                <th className="text-left py-2 px-2">operator</th>
                <th className="text-left py-2 px-2">ip</th>
                <th className="text-left py-2 px-2">device</th>
                <th className="text-left py-2 px-2">user-agent</th>
                <th className="text-left py-2 px-2">event</th>
                <th className="text-left py-2 px-2">status</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-[var(--txt-muted)]">// no attempts logged yet</td></tr>
              )}
              {entries.map((e) => {
                const ipDisplay = (e.ip || '').startsWith('raw:')
                  ? e.ip.replace('raw:', '')
                  : (e.ip || '').replace('hash:', '');
                const isRaw = (e.ip || '').startsWith('raw:');
                const evt = e.event || 'login';
                return (
                <tr key={e.id} className="border-b border-[var(--border-default)]" data-testid={`audit-row-${e.id}`}>
                  <td className="py-2 px-2 text-[var(--txt-secondary)] tabular">{cstDateTime(e.timestamp)}</td>
                  <td className="py-2 px-2 text-[var(--txt-muted)] text-[11px] live-num">{relAge(e.timestamp, now, clockOffsetMs)}</td>
                  <td className="py-2 px-2">{e.username}</td>
                  <td
                    className={`py-2 px-2 text-[10px] ${isRaw ? 'text-sell font-bold' : 'text-[var(--txt-muted)]'}`}
                    title={isRaw ? 'Unknown operator — raw IP stored for forensic investigation' : 'Known operator — SHA-256 hash only, raw IP never stored'}
                  >
                    {ipDisplay} {isRaw ? <span className="text-[9px] uppercase tracking-wider ml-1">raw</span> : <span className="text-[9px] uppercase tracking-wider ml-1 text-[var(--txt-muted)]">hash</span>}
                  </td>
                  <td className="py-2 px-2 uppercase text-[10px]">{e.device}</td>
                  <td className="py-2 px-2 text-[10px] text-[var(--txt-secondary)] truncate max-w-[240px]">{e.user_agent}</td>
                  <td className="py-2 px-2 text-[10px] uppercase tracking-wider">
                    <span className={evt === 'visit' ? 'text-cyan-sys' : 'text-[var(--txt-secondary)]'}>{evt}</span>
                  </td>
                  <td className={`py-2 px-2 font-bold ${e.success ? 'text-buy' : 'text-sell'}`}>
                    {e.success ? 'GRANTED' : 'DENIED'}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </CornerPanel>
    </div>
  );
}
